// ── Removable served "Now" (MA-89, ADR-041 §4 / ADR-052) ──
//
// The parent skill tree already lets a parent ADD intent via the ordered
// assignedSkillIds queue (assignedSkills.ts). This module is the SUBTRACTIVE
// counterpart: a per-child, UNORDERED set of skill ids the parent has
// removed from the ENGINE's own "Now" pick (a practicing/next skill the
// engine would otherwise serve, not something the parent asked for). Order
// doesn't matter for a removal set, so unlike AssignedSkillQueue this is a
// plain de-duped array used as set-membership, never walked head->tail.
//
// This module is the PURE core (no React, no localStorage, no Supabase) so
// the toggle rules are unit-testable in isolation, mirroring assignedSkills.ts.
// The storage shape lives on Progress (App/progress.ts); the read/inject
// point is useTodaysMission -> buildTodaysMission (progression.ts); the
// write point is the SkillTree screen.
//
// ── Design decisions encoded here ────────────────────────────────────
//  • MEMBERSHIP, not order. toggleSuppressedSkill is a pure add/remove;
//    there is no queue-head/FIFO semantics like assignedSkillIds.
//  • NO auto-prune. Unlike the assigned queue (which drops an entry once
//    the kid is "done" with it), a suppression stays until the parent
//    explicitly restores it via the SAME toggle function ("Add back") —
//    the parent's removal is a standing preference, not a one-shot nudge.
//  • DISJOINT from assignedSkillIds by construction. SkillTree.tsx's
//    toggleAssign/toggleSuppress each strip the id from the OTHER set when
//    writing, so a skill is never simultaneously assigned (parent opted IN)
//    and suppressed (parent opted OUT) even if a future UI path allowed
//    both toggles to reach the same row.
//  • NULL-SAFE. A child with no suppressedSkillIds field (every existing
//    user) reads as an empty set; nothing about prior behavior changes
//    until a parent removes a served "Now".

/** The persisted set shape. A plain de-duped list of micro-skill ids
 *  (e.g. "ms.math.11"). Stored on Progress.suppressedSkillIds. */
type SuppressedSkillSet = string[];

/** Normalize an untrusted persisted value into a clean set: an array of
 *  non-empty strings, de-duped (first occurrence wins), with falsy/garbage
 *  entries dropped. Legacy/absent -> []. Mirrors normalizeAssignedQueue. */
export function normalizeSuppressedSet(raw: unknown): SuppressedSkillSet {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v !== "string") continue;
    const id = v.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Toggle a skill's suppression. If present -> remove it (parent tapped
 *  "Add back", the skill can be served again). If absent -> add it (parent
 *  removed this served "Now" from the mission). Returns a NEW array; never
 *  mutates the input. */
export function toggleSuppressedSkill(
  set: SuppressedSkillSet,
  skillId: string,
): SuppressedSkillSet {
  const norm = normalizeSuppressedSet(set);
  if (norm.includes(skillId)) return norm.filter((id) => id !== skillId);
  return [...norm, skillId];
}

/** MA-395 (suppressed-skill-ids-cloud-sync slice) — union a local suppressed
 *  set with an untrusted cloud array, keeping local entries first (stable,
 *  deterministic — order carries no meaning for a set, per this file's own
 *  "MEMBERSHIP, not order" contract, but determinism matters for
 *  testability), appending any cloud id not already present. Mirrors
 *  assignedSkills.ts's mergeCloudAssignedSkills, minus the order-
 *  preservation contract (this is membership, not a FIFO queue). `cloudIds`
 *  may be anything untrusted; normalized via normalizeSuppressedSet. Pure. */
export function mergeSuppressedSets(
  localSet: SuppressedSkillSet,
  cloudIds: unknown,
): SuppressedSkillSet {
  const local = normalizeSuppressedSet(localSet);
  const cloud = normalizeSuppressedSet(cloudIds);
  if (cloud.length === 0) return local;
  const seen = new Set(local);
  const merged = [...local];
  for (const id of cloud) {
    if (seen.has(id)) continue;
    seen.add(id);
    merged.push(id);
  }
  return merged;
}
